import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  ConsentBodyListSchema,
  ConsentBodySchema,
  UpdateConsentBodySchema,
  type ConsentBody,
  type ConsentBodyList,
  type UpdateConsentBodyInput,
} from '@datazo/contracts';
import { apiFetch } from '@/shared/api/client';

const keys = {
  all: ['consent-bodies'] as const,
  list: () => [...keys.all, 'list'] as const,
  one: (cups: string) => [...keys.all, 'one', cups] as const,
};

export function useConsentBodiesList(): UseQueryResult<ConsentBodyList, Error> {
  return useQuery({
    queryKey: keys.list(),
    queryFn: () =>
      apiFetch<ConsentBodyList>('/api/admin/consent-bodies', {
        parseAs: ConsentBodyListSchema,
      }),
    staleTime: 30_000,
  });
}

export function useConsentBody(cups: string | undefined): UseQueryResult<ConsentBody, Error> {
  return useQuery({
    queryKey: keys.one(cups ?? ''),
    queryFn: () =>
      apiFetch<ConsentBody>(`/api/admin/consent-bodies/${encodeURIComponent(cups!)}`, {
        parseAs: ConsentBodySchema,
      }),
    enabled: cups !== undefined && cups !== '',
    staleTime: 30_000,
  });
}

export function useUpdateConsentBody(
  cups: string,
): UseMutationResult<ConsentBody, Error, UpdateConsentBodyInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateConsentBodyInput) => {
      const body = UpdateConsentBodySchema.parse(input);
      return apiFetch<ConsentBody>(
        `/api/admin/consent-bodies/${encodeURIComponent(cups)}`,
        { method: 'PUT', body, parseAs: ConsentBodySchema },
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.all });
    },
  });
}
